import {
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  type FormProps,
  Input,
  Row,
  Space,
} from 'antd';

import IconFilter from '@/assets/icons/filter.svg?react';
import { FormItem } from '@/components';

import {
  ORGANIZATION_ACODE_STATUS_OPTIONS,
  ORGANIZATION_BENEFICIARY_ACCOUNT_STATUS_OPTIONS,
  ORGANIZATION_CONTRACT_STATUS_OPTIONS,
} from '../constants';
import { useOrganizationsOfMe } from '../queries/organization';
import type { OrganizationFiltersFormValues } from '../types';

interface OrganizationFiltersProps
  extends Omit<FormProps, 'onFinish' | 'onReset'> {
  filtersValues: Partial<OrganizationFiltersFormValues>;
  onFinish(): void;
  onReset(): void;
  setFilterValues: React.Dispatch<
    React.SetStateAction<Partial<OrganizationFiltersFormValues>>
  >;
}

export function OrganizationFilters({
  filtersValues,
  onReset,
  onFinish,
  setFilterValues,
  ...rest
}: Readonly<OrganizationFiltersProps>) {
  const [form] = Form.useForm();
  const { organizations } = useOrganizationsOfMe();
  const name = Form.useWatch('id', form);
  const contractStatus = Form.useWatch('contract_status', form);
  const acodeStatus = Form.useWatch('acode_status', form);
  const beneficiaryAccountStatus = Form.useWatch(
    'beneficiary_account_status',
    form,
  );

  const organizationsOptions = organizations?.map(({ id, name }) => ({
    id,
    value: name,
  }));

  const organizationId = organizationsOptions?.find(
    ({ value }) => value === name,
  )?.id;

  const isDisableSearch =
    !organizationId &&
    !contractStatus?.length &&
    !acodeStatus?.length &&
    !beneficiaryAccountStatus?.length;

  function handleFinish(values: OrganizationFiltersFormValues) {
    onFinish?.();
    setFilterValues({ ...values, id: organizationId });
  }

  function handleReset() {
    onReset?.();
    setFilterValues({});
  }

  return (
    <Form
      form={form}
      className="filters-form mb-16"
      layout="vertical"
      onReset={handleReset}
      onFinish={handleFinish}
      {...rest}
    >
      <Card variant="borderless" size="small">
        <Row>
          <Col>
            <FormItem label="チェーン名" layout="horizontal" name="id">
              <AutoComplete
                options={organizationsOptions}
                filterOption={(inputValue, option) =>
                  option!.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              >
                <Input placeholder="チェーン名で検索" />
              </AutoComplete>
            </FormItem>
          </Col>
        </Row>
        <Row gutter={[24, 16]} className="mt-16" align="bottom">
          <Col>
            <FormItem name="contract_status" label="ステータス">
              <Checkbox.Group>
                {ORGANIZATION_CONTRACT_STATUS_OPTIONS.map(
                  ({ label, value }) => (
                    <Checkbox value={value} key={label}>
                      {label}
                    </Checkbox>
                  ),
                )}
              </Checkbox.Group>
            </FormItem>
          </Col>
          <Col>
            <FormItem name="acode_status" label="Aコード">
              <Checkbox.Group>
                {ORGANIZATION_ACODE_STATUS_OPTIONS.map(({ label, value }) => (
                  <Checkbox value={value} key={label}>
                    {label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </FormItem>
          </Col>
          <Col>
            <FormItem name="beneficiary_account_status" label="受取人口座">
              <Checkbox.Group>
                {ORGANIZATION_BENEFICIARY_ACCOUNT_STATUS_OPTIONS.map(
                  ({ label, value }) => (
                    <Checkbox value={value} key={label}>
                      {label}
                    </Checkbox>
                  ),
                )}
              </Checkbox.Group>
            </FormItem>
          </Col>
          <Col className="ml-auto">
            <Space>
              <Button
                htmlType="reset"
                disabled={
                  isDisableSearch &&
                  !Object.values(filtersValues).some(value =>
                    typeof value === 'number' ? value : value?.length,
                  )
                }
              >
                クリア
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<IconFilter />}
                disabled={isDisableSearch}
              >
                絞り込み
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
    </Form>
  );
}
